import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import prependHttp from 'prepend-http';
import { Box, Divider, Hidden, Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import { multilineEllipsis } from '../../theme/styles/mixins';
import Link from '../Link';
import ContentCover from '../Content/ContentCover';
import ThemableBackgroundWithIcon from '../../theme/components/Background/ThemableBackgroundWithIcon';
import useDarkTheme from '../../theme/styles/useDarkTheme';
import CoverTile from '../CoverTile';
import ShowMoreLess from '../Content/ShowMoreLess';
import ContentStatusTag, {
  ContentStatusSizeType,
  ContentStatusType,
} from '../ContentStatusTag';

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    minHeight: theme.spacing(7),
    width: theme.spacing(7),
  },
  blockCover: {
    minHeight: theme.spacing(48.5),
  },
  blockLink: {
    '&:hover': {
      color: 'var(--text-primary-light)',
    },
  },
  cardDescription: {
    display: 'column',
    padding: theme.spacing(2, 2, 2, 0),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
  },
  cover: {
    minHeight: theme.spacing(18.5),
    position: 'relative',
  },
  coverTag: {
    backgroundColor: 'var(--content-overlay-black)',
    borderRadius: theme.spacing(0.5, 0, 0, 0),
    bottom: 0,
    padding: theme.spacing(0.75, 1.5),
    position: 'absolute',
    right: 0,
  },
  coverCard: {
    borderRadius: theme.spacing(0.5),
    maxWidth: '100%',
    minWidth: theme.spacing(29),
    objectFit: 'cover',
    [theme.breakpoints.up('sm')]: {
      maxHeight: theme.spacing(14),
      maxWidth: theme.spacing(30),
    },
  },
  icon: {
    color: theme.palette.primary.contrastText,
  },
  title: {
    ...multilineEllipsis(2),
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: theme.spacing(2),
    lineHeight: theme.spacing(3),
    letterSpacing: '0.15px',
  },
  urlCard: {
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  wrapper: {
    border: '1px solid var(--input-box)',
    borderRadius: theme.spacing(0.5),
    cursor: 'pointer',
    display: 'block',
    [theme.breakpoints.down('lg')]: {
      minHeight: 'auto',
    },
  },
  wrapperGrid: {
    cursor: 'pointer',
    minHeight: theme.spacing(13.75),
  },
}));

export enum UrlViewerVariant {
  Block = 'block',
  Card = 'card',
}

export interface UrlViewerProps {
  actionSpace?: boolean;
  className?: string;
  cover?: string;
  darkTheme?: boolean;
  description?: string;
  /**
   * Whether to force open the url in a new tab, even if the url has been
   * specified to be opened in an iframe. This is used wherever the piece
   * of content is displayed outside of rooms, where the fullscreen iframe
   * behavior doesn't make sense
   */
  forceOpenInNewTab?: boolean;
  iframe?: boolean;
  isExpired?: boolean;
  name: string;
  onClick?: () => void;
  onView?: () => void;
  url: string;
  variant?: UrlViewerVariant;
  track?: boolean;
}

const UrlViewer: React.FC<UrlViewerProps> = (
  props: UrlViewerProps
): JSX.Element => {
  const classes = useStyles();
  const darkThemeClasses = useDarkTheme();

  const {
    actionSpace,
    className,
    cover,
    darkTheme = false,
    description,
    forceOpenInNewTab,
    iframe,
    isExpired,
    name,
    onClick,
    onView,
    url,
    track,
    variant = UrlViewerVariant.Card,
  } = props;

  const fullUrl = useMemo(() => prependHttp(url), [url]);

  const isBlockVariant = useMemo(
    () => variant === UrlViewerVariant.Block,
    [variant]
  );

  useEffect(() => {
    if (onView) {
      onView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    if (iframe && !forceOpenInNewTab) {
      e.preventDefault();
    }

    if (onClick) {
      onClick();
    }
  };

  const renderBlockCover = (tag?: React.ReactNode) => {
    if (cover) {
      return (
        <Box
          className={classNames(classes.cover, {
            [classes.blockCover]: isBlockVariant,
          })}
        >
          <ContentCover clickable cover={cover} />
          {tag}
        </Box>
      );
    }

    return (
      <ThemableBackgroundWithIcon
        wrapperClassName={classes.wrapperGrid}
        avatarClassName={classes.avatar}
      >
        <LinkIcon className={classes.icon} />
      </ThemableBackgroundWithIcon>
    );
  };

  const renderStatusTag = (): React.ReactNode => {
    if (track || !isExpired) {
      return;
    }

    return (
      <ContentStatusTag
        size={ContentStatusSizeType.Default}
        statusType={ContentStatusType.Expired}
        sx={(theme) => ({
          left: theme.spacing(3),
          position: 'absolute',
          top: theme.spacing(3.5),
        })}
      />
    );
  };

  const renderCardCover = () => (
    <CoverTile>
      {({ height, width }) => (
        <Box height="auto" padding="16px">
          {cover ? (
            <img
              alt="Url content cover"
              className={classes.coverCard}
              src={cover}
              // subtract the padding from the height
              style={{ height: height - 16, width }}
            />
          ) : (
            <Box className={classes.coverCard}>
              <ThemableBackgroundWithIcon
                avatarClassName={classes.avatar}
                wrapperClassName={classes.wrapperGrid}
              >
                <LinkIcon className={classes.icon} />
              </ThemableBackgroundWithIcon>
            </Box>
          )}
          {renderStatusTag()}
        </Box>
      )}
    </CoverTile>
  );

  if (isBlockVariant) {
    return (
      <Box>
        <Box alignItems="baseline" display="flex">
          <Box flex="1">
            <Typography
              className={classNames({
                [darkThemeClasses.primaryTextLight]: darkTheme,
              })}
              variant="h6"
            >
              {name}
            </Typography>
          </Box>
          <Hidden mdDown>
            <Link
              afterIcon={<OpenInNewOutlined color="inherit" />}
              className={classNames({
                [classes.blockLink]: darkTheme,
                [darkThemeClasses.primaryTextLight]: darkTheme,
              })}
              href={fullUrl}
              onClick={handleClick}
              text="Visit link"
            />
          </Hidden>
        </Box>
        {description && (
          <Box mt={1}>
            <Typography
              className={classNames({
                [darkThemeClasses.secondaryTextLight]: darkTheme,
              })}
              variant="body1"
            >
              {description}
            </Typography>
          </Box>
        )}
        <Box mt={name || description ? 4 : 0}>
          {!cover ? (
            <Divider
              className={classNames({ [darkThemeClasses.divider]: darkTheme })}
            />
          ) : (
            <a
              href={fullUrl}
              onClick={handleClick}
              rel="noreferrer"
              target="_blank"
            >
              {renderBlockCover(
                <Box className={classes.coverTag}>
                  <Link
                    afterIcon={
                      <OpenInNewOutlined color="inherit" fontSize="small" />
                    }
                    className={classNames(classes.blockLink, {
                      [darkThemeClasses.primaryTextLight]: darkTheme,
                    })}
                    text="Visit link"
                    variant="body2"
                  />
                </Box>
              )}
            </a>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Typography
      className={classNames(className, classes.wrapper)}
      component="a"
      display="flex"
      href={fullUrl}
      onClick={handleClick}
      rel="noreferrer"
      target="_blank"
    >
      <Box className={classes.urlCard}>
        {renderCardCover()}
        <Box className={classes.cardDescription}>
          <Box
            className="UrlViewer__title"
            mb={0.5}
            mr={actionSpace ? 10.75 : 0}
          >
            <Typography className={classes.title} color="secondary">
              {name}
            </Typography>
          </Box>
          <Box flexDirection="row">
            {description && (
              <ShowMoreLess text={description}>{description}</ShowMoreLess>
            )}
          </Box>
        </Box>
      </Box>
    </Typography>
  );
};

export default UrlViewer;
